import { Link } from "react-router-dom";
import { formatCPF, formatMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, Pencil, Trash2, Loader2, FileArchive, Star } from "lucide-react";
import Highlight from "@/pages/inscricoes/Highlight";

const statusColor = (s) => ({
  "Recebida": "bg-blue-100 text-blue-800", "Em Triagem": "bg-yellow-100 text-yellow-800",
  "Documentação Pendente": "bg-orange-100 text-orange-800", "Em Análise": "bg-purple-100 text-purple-800",
  "Aprovada": "bg-green-100 text-green-800", "Indeferida": "bg-red-100 text-red-800",
  "Homologada": "bg-emerald-100 text-emerald-800",
}[s] || "bg-slate-100");

function BulkBar({ selected, canDelete, canDossie, bulkDossieLoading, onClear, onDossie, onDelete }) {
  if (!canDossie || selected.size === 0) return null;
  return (
    <div className="bg-blue-50 border-b border-blue-200 p-3 flex items-center justify-between flex-wrap gap-2" data-testid="bulk-action-bar">
      <div className="text-sm">
        <strong>{selected.size}</strong> {selected.size === 1 ? "inscrição selecionada" : "inscrições selecionadas"}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={onClear} data-testid="btn-bulk-clear">
          Limpar seleção
        </Button>
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={onDossie}
          disabled={bulkDossieLoading}
          data-testid="btn-bulk-dossie"
          title="Gera um ZIP com o Dossiê (PDF) de cada inscrição selecionada"
        >
          {bulkDossieLoading
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : <FileArchive className="w-4 h-4 mr-2" />}
          Baixar {selected.size} Dossiê(s) em ZIP
        </Button>
        {canDelete && (
          <Button size="sm" variant="destructive" onClick={onDelete} data-testid="btn-bulk-delete">
            <Trash2 className="w-4 h-4 mr-2" /> Excluir {selected.size} em lote
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({ it, idx, q, canDossie, canEdit, canDelete, selected, togglingFav,
                onToggleOne, onToggleFav, onAskDelete }) {
  return (
    <tr className={idx % 2 ? "bg-slate-50" : ""} data-testid={`row-${it.id}`}>
      {canDossie && (
        <td className="p-3">
          <Checkbox
            checked={selected.has(it.id)}
            onCheckedChange={() => onToggleOne(it.id)}
            aria-label={`Selecionar ${it.protocolo}`}
            data-testid={`chk-row-${it.id}`}
          />
        </td>
      )}
      <td className="p-3 font-mono text-xs">
        <Highlight text={it.protocolo} query={q} />
      </td>
      <td className="p-3 font-medium">
        <div className="flex items-center gap-1.5">
          {it.is_favorite && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500 shrink-0" aria-label="Favorita" />}
          <Highlight text={it.nome_completo} query={q} />
        </div>
      </td>
      <td className="p-3 font-mono text-xs">
        <Highlight text={formatCPF(it.cpf)} query={q} cpfMode />
      </td>
      <td className="p-3"><Highlight text={it.bairro} query={q} /></td>
      <td className="p-3">{formatMoney(it.renda_total)}</td>
      <td className="p-3"><Badge className={statusColor(it.status)}>{it.status}</Badge></td>
      <td className="p-3">
        <div className="flex gap-2">
          {canDossie && (
            <Button
              size="sm"
              variant="outline"
              className={it.is_favorite
                ? "border-amber-400 text-amber-600 hover:bg-amber-50"
                : "text-slate-400 hover:text-amber-600 hover:border-amber-300"}
              onClick={() => onToggleFav(it)}
              disabled={togglingFav === it.id}
              data-testid={`btn-fav-${it.id}`}
              title={it.is_favorite ? "Desfavoritar" : "Favoritar"}
            >
              <Star className={`w-4 h-4 ${it.is_favorite ? "fill-current" : ""}`} />
            </Button>
          )}
          <Link to={`/admin/inscricoes/${it.id}`}>
            <Button size="sm" variant="outline" data-testid={`btn-ver-${it.id}`} title="Visualizar"><Eye className="w-4 h-4" /></Button>
          </Link>
          {canEdit && (
            <Link to={`/admin/inscricoes/${it.id}/editar`}>
              <Button size="sm" variant="outline" className="border-[#083CA6] text-[#083CA6] hover:bg-[#083CA6] hover:text-white" data-testid={`btn-editar-${it.id}`} title="Editar Inscrição">
                <Pencil className="w-4 h-4" />
              </Button>
            </Link>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white"
              data-testid={`btn-delete-${it.id}`}
              title="Excluir inscrição (backup automático)"
              onClick={() => onAskDelete(it)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function InscricoesTable({
  items, q, selected, togglingFav, bulkDossieLoading,
  canDossie, canEdit, canDelete,
  onToggleOne, onToggleAll, onToggleFav, onAskDelete,
  onClearSelection, onBulkDossie, onBulkDelete,
}) {
  const colSpan = canDossie ? 8 : 7;
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <BulkBar
          selected={selected}
          canDelete={canDelete}
          canDossie={canDossie}
          bulkDossieLoading={bulkDossieLoading}
          onClear={onClearSelection}
          onDossie={onBulkDossie}
          onDelete={onBulkDelete}
        />
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left">
            <tr>
              {canDossie && (
                <th className="p-3 w-10">
                  <Checkbox
                    checked={items.length > 0 && selected.size === items.length}
                    onCheckedChange={onToggleAll}
                    aria-label="Selecionar todos"
                    data-testid="chk-select-all"
                  />
                </th>
              )}
              <th className="p-3">Protocolo</th>
              <th className="p-3">Nome</th>
              <th className="p-3">CPF</th>
              <th className="p-3">Bairro</th>
              <th className="p-3">Renda</th>
              <th className="p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <Row
                key={it.id}
                it={it}
                idx={idx}
                q={q}
                canDossie={canDossie}
                canEdit={canEdit}
                canDelete={canDelete}
                selected={selected}
                togglingFav={togglingFav}
                onToggleOne={onToggleOne}
                onToggleFav={onToggleFav}
                onAskDelete={onAskDelete}
              />
            ))}
            {items.length === 0 && (
              <tr><td colSpan={colSpan} className="p-8 text-center text-slate-500">Nenhuma inscrição encontrada.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
